import { useEffect, useState, type ReactNode } from 'react';
import { Fingerprint as FingerprintIcon, Copy, Check, Loader2, History } from 'lucide-react';
import contractService from '../../../services/contracts';
import { formatDateTimeInTz, useOrgTimezone } from '../../../utils/timezone';
import {
  CONTRACT_EVENT_LABELS, shortHash,
  type ContractDiffLine, type ContractEvent, type ContractEventKind,
} from '../../../types/contracts';

/**
 * The SHA-256 of a text, shortened to read aloud, with the full value one tap
 * away. The same value is printed on the PDF and returned by the public verify
 * page, which is what lets anyone holding a copy check it is the one on record.
 */
export function Fingerprint({ hash, label = 'Fingerprint' }: { hash: string | null | undefined; label?: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!hash) return <span className="text-xs text-neutral-400">No fingerprint recorded</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the value is on screen */ }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs text-neutral-600 min-w-0 max-w-full">
      <FingerprintIcon className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
      <span className="text-neutral-500">{label}:</span>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="font-mono text-neutral-800 hover:underline break-all text-left"
        title="Show the full SHA-256"
      >
        {open ? hash : shortHash(hash)}
      </button>
      <button type="button" onClick={copy} className="p-0.5 text-neutral-400 hover:text-neutral-700" aria-label="Copy fingerprint">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

/** A line diff, as returned by GET /cultivar/contracts/:id/diff. */
export function ContractDiffView({ lines, context = 2 }: { lines: ContractDiffLine[]; context?: number }) {
  const keep = lines.map(() => false);
  lines.forEach((l, i) => {
    if (l.op === 'same') return;
    for (let k = Math.max(0, i - context); k <= Math.min(lines.length - 1, i + context); k++) keep[k] = true;
  });
  const changed = lines.some(l => l.op !== 'same');
  if (!changed) return <p className="text-sm text-neutral-500">The two versions are word-for-word identical.</p>;

  const out: ReactNode[] = [];
  let gap = false;
  lines.forEach((l, i) => {
    if (!keep[i]) {
      if (!gap) out.push(<div key={`g${i}`} className="text-neutral-400 px-2 py-0.5">⋯</div>);
      gap = true;
      return;
    }
    gap = false;
    const cls = l.op === 'add'
      ? 'bg-emerald-50 text-emerald-900'
      : l.op === 'remove'
        ? 'bg-red-50 text-red-900 line-through decoration-red-300'
        : 'text-neutral-600';
    const mark = l.op === 'add' ? '+' : l.op === 'remove' ? '−' : ' ';
    out.push(
      <div key={i} className={`flex gap-2 px-2 py-0.5 ${cls}`}>
        <span className="select-none w-3 flex-shrink-0 text-neutral-400">{mark}</span>
        <span className="whitespace-pre-wrap break-words min-w-0">{l.text || ' '}</span>
      </div>
    );
  });
  return <div className="font-mono text-xs border border-neutral-200 rounded-lg overflow-hidden">{out}</div>;
}

/**
 * The record of what happened to a contract, agreement, programme, group or
 * order. Append-only on the server; this only reads it.
 */
export function ContractHistory({ kind, id, title = 'History', refreshKey = 0 }: {
  kind: ContractEventKind; id: number; title?: string; refreshKey?: number;
}) {
  const orgTz = useOrgTimezone();
  const [events, setEvents] = useState<ContractEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    contractService.events(kind, id)
      .then(res => { if (!cancelled) setEvents(res.success ? (res.data ?? []) : []); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the history'); });
    return () => { cancelled = true; };
  }, [kind, id, refreshKey]);

  return (
    <div>
      <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2 mb-2">
        <History className="h-4 w-4 text-neutral-400" /> {title}
      </h3>
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : events === null ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : events.length === 0 ? (
        <p className="text-sm text-neutral-500">Nothing recorded yet.</p>
      ) : (
        <ol className="space-y-2">
          {events.map(e => {
            const hasDetail = !!(e.ip_address || e.user_agent || (e.details && Object.keys(e.details).length > 0));
            return (
              <li key={e.id} className="border-l-2 border-neutral-200 pl-3 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-sm font-medium text-neutral-900">{CONTRACT_EVENT_LABELS[e.action] || e.action}</span>
                  <span className="text-xs text-neutral-500">{formatDateTimeInTz(e.created_at, orgTz)}</span>
                  {e.actor_name && <span className="text-xs text-neutral-500">· {e.actor_name}</span>}
                </div>
                {e.summary && <p className="text-sm text-neutral-700 break-words">{e.summary}</p>}
                {e.content_hash && <Fingerprint hash={e.content_hash} />}
                {hasDetail && (
                  <button
                    type="button"
                    onClick={() => setExpanded(x => (x === e.id ? null : e.id))}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    {expanded === e.id ? 'Hide detail' : 'Detail'}
                  </button>
                )}
                {expanded === e.id && (
                  <div className="mt-1 text-xs text-neutral-600 space-y-1 min-w-0">
                    {e.ip_address && <p>IP: <span className="font-mono">{e.ip_address}</span></p>}
                    {e.user_agent && <p className="break-words">Device: {e.user_agent}</p>}
                    <EventDetail details={e.details} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// Long texts kept for the record (a draft's before and after) are shown as a
// size, not dumped inline; the version page shows them properly.
function EventDetail({ details }: { details: Record<string, unknown> }) {
  const entries = Object.entries(details || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
      {entries.map(([k, v]) => {
        let shown: string;
        if (typeof v === 'string') shown = v.length > 240 ? `${v.length.toLocaleString()} characters of text` : v;
        else shown = JSON.stringify(v);
        if (shown.length > 400) shown = `${shown.slice(0, 400)}…`;
        return (
          <div key={k} className="contents">
            <dt className="text-neutral-400">{k.replace(/_/g, ' ')}</dt>
            <dd className="break-words font-mono min-w-0">{shown}</dd>
          </div>
        );
      })}
    </dl>
  );
}
